import cls from "classnames";
import {useEffect} from "react";
import toast from "react-hot-toast";

import {closeCheckout, emitOnPaymentSuccess} from "helpers/postmessage/emitters";
import * as Posthog from "@qoohoo/common/utils/analytics/posthog";

import BackChevronIcon from "components/icons/BackChevronIcon";
import CrossIcon from "components/icons/CrossIcon";
import PlanSelection from "components/screens/PlanSelection/PlanSelection";
import Success from "components/screens/success/Success";
import ScreenLoader from "components/loaders/ScreenLoader";
import PaymentGateway from "components/PaymentGateways/PaymentGateway";
import CustomerForm2 from "components/screens/customerForm2/CustomerForm2";
import CustomerForm1 from "components/screens/customerForm1/CustomerForm1";
import Portal from "components/Portal";

import getPurchaseDetails from "utils/getPurchaseDetails";
import openPaymentGateway from "utils/openPaymentGateway";

import ScreenTypes from "constants/screenTypes";
import useAppState from "store/appState";
import usePreferencesStore from "store/preferencesStore";
import usePurchaseState from "store/purchaseState";
import useModalPortalState from "store/modalPortalState";
import useEntityStore from "store/entityStore";

export default function MiniCheckoutContent() {
  const [
    canCustomerCloseCheckout, order, isOrderCreating,
    isPaymentGatewayOpened, isPollingForPayment, isPaymentSuccessConfirmed,
    activeScreen, setActiveScreen, setIsPaymentGatewayOpened
  ] = useAppState(state => [
    state.canCustomerCloseCheckout, state.order, state.isOrderCreating,
    state.isPaymentGatewayOpened, state.isPollingForPayment, state.isPaymentSuccessConfirmed,
    state.activeScreen, state.setActiveScreen, state.setIsPaymentGatewayOpened
  ]);

  const entity = useEntityStore(state => state.entity?.data);
  const purchaseState = usePurchaseState(state => state);
  const preferences = usePreferencesStore(state => state.preferences?.data);
  const isModalPortalOpened = useModalPortalState(state => state.isOpened);

  function handleCheckoutCloseAnalytics() {
    const data = {
      entity_uid: entity?.uid,
      entity_type: entity?.type,
      creator_uid: entity?.creator?.uid,
      entity_name: entity?.name,
      current_screen: "checkout_view",
      current_route: window.location.pathname,
      payment_gateway: entity?.payment?.paymentGateway,
      total_amount:
        entity?.payment?.plans?.[0]?.data?.amount_after_discount ??
        entity?.payment?.plans?.[0]?.data?.amount,
      currency: entity?.payment?.currency,
      subscription_plan_interval: entity?.payment?.plans?.[0]?.data?.interval,
      subscription_plan_duration: entity?.payment?.plans?.[0]?.data?.duration,
      platform: "web",
    };

    console.log("Posthog button click data: payment_cancelled", data);

    Posthog.buttonClick("payment_cancelled", data);
  }

  function handleCheckoutClose() {
    // TODO: Handle for stale data also, else reload the data

    // TODO: Show confirmation before closing.
    // TODO: Critical, show custom confirmation like - coupon might not exist forever
    handleCheckoutCloseAnalytics();
    closeCheckout();
  }

  useEffect(() => {
    if(!order) return;
    
    if (order.isRecurring) {
      setActiveScreen(ScreenTypes.PLAN_SELECTION);
    } else {
      setActiveScreen(ScreenTypes.CUSTOMER_FORM_1);
    }
  }, [order?.isRecurring]);

  async function onPaymentSuccess() {
    emitOnPaymentSuccess();

    await getPurchaseDetails();
  }

  // TODO: See if we can avoid useEffect and put openPaymentGateway directly where isPaymentgatewayOpened is true
  useEffect(() => {
    // console.log('isPaymentGatewayOpened....', isPaymentGatewayOpened);
    if (isPaymentGatewayOpened) {
      openPaymentGateway(onPaymentSuccess)
        .catch((err) => {
          toast.error(err?.message);
          
          setIsPaymentGatewayOpened(false);
        });
    }
  }, [isPaymentGatewayOpened]);

  // TODO: See if we can avoid useEffect and put transitionScreenOnPaymentSuccess directly after purchase data fetch
  useEffect(() => {
    if (isPaymentSuccessConfirmed && purchaseState.purchase?.data) {
      transitionScreenOnPaymentSuccess();
    }
  }, [isPaymentSuccessConfirmed, purchaseState]);

  async function transitionScreenOnPaymentSuccess() {
    try {
      if (preferences.postPaymentForm && !purchaseState.purchase?.data.isPostPaymentFormSubmitted) {
        setActiveScreen(ScreenTypes.CUSTOMER_FORM_2);
      } else {
        setActiveScreen(ScreenTypes.PAYMENT_SUCCESS);
      }
    } catch(err) {
      console.log(err);
      toast.error("Oops, please reload the page! Your payment is successful");
    }
  }

  function getOverlayTitle() {
    let overlayTitle, hideLoader = false;

    const isAnyLoadingState = isOrderCreating || isPaymentGatewayOpened || isPollingForPayment || purchaseState.isLoading;

    if (!isAnyLoadingState) {
      return {
        overlayTitle: false,
        hideLoader
      };
    }

    if (isOrderCreating) {
      overlayTitle = "Initiating booking...";
    } else if (isPaymentGatewayOpened) {
      overlayTitle = "Payment in progress...";
      hideLoader = true;
    } else if (isPollingForPayment || purchaseState.isLoading) {
      overlayTitle = "Registering, please wait...";
    }

    return {overlayTitle, hideLoader};
  }

  const {overlayTitle, hideLoader} = getOverlayTitle();
  const showOverlay = !!overlayTitle || isModalPortalOpened;

  let _ActiveScreenComponent, goBack;

  switch(activeScreen) {
    case ScreenTypes.PLAN_SELECTION: {
      _ActiveScreenComponent = PlanSelection;
      break;
    }
    case ScreenTypes.CUSTOMER_FORM_1: {
      _ActiveScreenComponent = CustomerForm1;

      if (order.isRecurring) {
        goBack = () => setActiveScreen(ScreenTypes.PLAN_SELECTION);
      }

      break;
    }
    case ScreenTypes.CUSTOMER_FORM_2: {
      _ActiveScreenComponent = CustomerForm2;
      break;
    }
    case ScreenTypes.PAYMENT_SUCCESS: {
      _ActiveScreenComponent = Success;
      break;
    }
  }

  return (
    <>
      {
        entity && (
          <PaymentGateway
            type={entity.payment.paymentGateway}
          />
        )
      }
      {showOverlay && <Overlay title={overlayTitle} hideLoader={hideLoader} />}
      <div id="content" className={cls("h-full w-full flex flex-col relative")}>
        {_ActiveScreenComponent?.title && (
          <ModalHeader
            title={_ActiveScreenComponent.title}
            goBack={goBack}
            handleClose={canCustomerCloseCheckout ? handleCheckoutClose : null}
          />
        )}
        {
          _ActiveScreenComponent && (
            <ModalBody>
              <_ActiveScreenComponent />
            </ModalBody>
          )
        }
        <Portal />
      </div>
    </>
  );
}

function ModalHeader({title, handleClose, goBack}) {
  return (
    <div id="header">
      <div className="flex flex-shrink-0 p-4 text-base font-bold justify-center shadow-[0_4px_10px_rgba(0,0,0,0.05)]">
        {
          goBack && (
            <button className="absolute left-4" onClick={goBack}>
              <BackChevronIcon />
            </button>
          )
        }
        <span>{title}</span>
        {
          handleClose && (
            <button className="absolute right-4" onClick={handleClose}>
              <CrossIcon />
            </button>
          )
        }
      </div>
    </div>
  );
}

function ModalBody({children}) {
  return (
    <div id="body" className="flex flex-1">
      {children}
    </div>
  )
}

function Overlay({title, hideLoader}) {
  return (
    <div id="overlay">
      {
        title && (
          <ScreenLoader title={title} hideLoader={hideLoader} />
        )
      }
    </div>
  )
}

